import API from "../../services/api";
import { queryClient } from "../../lib/queryClient";
import { extractApiErrorMessage } from "../../utils/guidedErrors";

function errMsg(e, fallback) {
  return extractApiErrorMessage(e, fallback);
}

export async function initLeaveModule({ organisation_id, year, enabled_states }) {
  const { data } = await API.post("/leave/init", {
    organisation_id,
    year,
    enabled_states,
  });
  return data;
}

export async function fetchLeaveSettings(organisationId) {
  const { data } = await API.get(`/leave/settings/${organisationId}`);
  return data;
}

export async function updateLeaveSettings(organisationId, payload) {
  const { data } = await API.patch(`/leave/settings/${organisationId}`, payload);
  return data;
}

export async function fetchLeaveTypes(organisationId, { include_archived } = {}) {
  const { data } = await API.get("/leave/types", {
    params: { organisation_id: organisationId, include_archived },
  });
  return data;
}

export async function createLeaveType(organisationId, payload) {
  try {
    const { data } = await API.post("/leave/types", payload, {
      params: { organisation_id: organisationId },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to create leave type"));
  }
}

export async function updateLeaveType(organisationId, leaveTypeId, payload) {
  try {
    const { data } = await API.patch(`/leave/types/${leaveTypeId}`, payload, {
      params: { organisation_id: organisationId },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to update leave type"));
  }
}

export async function deleteLeaveType(organisationId, leaveTypeId) {
  try {
    const { data } = await API.delete(`/leave/types/${leaveTypeId}`, {
      params: { organisation_id: organisationId },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to delete leave type"));
  }
}

export async function fetchLeavePolicies(organisationId, leaveTypeId) {
  const { data } = await API.get("/leave/policies", {
    params: { organisation_id: organisationId, leave_type_id: leaveTypeId },
  });
  return data;
}

export async function createLeavePolicy(organisationId, payload) {
  try {
    const { data } = await API.post("/leave/policies", payload, {
      params: { organisation_id: organisationId },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to create leave policy"));
  }
}

export async function updateLeavePolicy(organisationId, policyId, payload) {
  try {
    const { data } = await API.patch(`/leave/policies/${policyId}`, payload, {
      params: { organisation_id: organisationId },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to update leave policy"));
  }
}

export async function deleteLeavePolicy(organisationId, policyId) {
  try {
    const { data } = await API.delete(`/leave/policies/${policyId}`, {
      params: { organisation_id: organisationId },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to delete leave policy"));
  }
}

/** The logged-in user's own linked employee record ({ linked, employee_id, name, … }). */
export async function fetchMyLeaveEmployee() {
  try {
    const { data } = await API.get("/leave/me/employee");
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to load your employee profile"));
  }
}

export async function applyLeaveRequest(payload) {
  try {
    const { data } = await API.post("/leave/requests", payload);
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to apply leave"));
  }
}

export async function fetchLeaveRequests({
  organisation_id,
  employee_id,
  status,
  actionable_only,
} = {}) {
  const { data } = await API.get("/leave/requests", {
    params: {
      organisation_id,
      employee_id,
      status,
      actionable_only: actionable_only ? 1 : undefined,
    },
  });
  return data;
}

export async function fetchMyLeaveQueue(organisationId, { employee_id } = {}) {
  const { data } = await API.get("/leave/requests/my-queue", {
    params: {
      organisation_id: organisationId,
      employee_id: employee_id || undefined,
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchActionableLeaveCount(organisationId, { fresh = false } = {}) {
  if (!organisationId) return 0;
  const queryKey = ["leave-actionable-count", organisationId];
  if (fresh) {
    await queryClient.invalidateQueries({ queryKey });
  }
  try {
    return await queryClient.fetchQuery({
      queryKey,
      staleTime: 20_000,
      queryFn: async () => {
        const { data } = await API.get("/leave/requests/actionable-count", {
          params: { organisation_id: organisationId },
        });
        return Number(data?.count) || 0;
      },
    });
  } catch {
    return 0;
  }
}

export async function approveLeaveRequest(requestId, { decision, notes, level }) {
  try {
    const { data } = await API.post(`/leave/requests/${requestId}/approve`, {
      decision,
      notes,
      level,
    });
    return data;
  } catch (e) {
    const wrapped = new Error(extractApiErrorMessage(e, "Failed to update leave"));
    wrapped.cause = e;
    throw wrapped;
  }
}

export async function cancelLeaveRequest(requestId) {
  try {
    const { data } = await API.post(`/leave/requests/${requestId}/cancel`);
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to cancel leave"));
  }
}

export async function fetchLeaveBalances({ organisation_id, employee_id, year }) {
  try {
    const { data } = await API.get("/leave/balances", {
      params: {
        organisation_id,
        ...(employee_id ? { employee_id } : {}),
        ...(year != null ? { year } : {}),
      },
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to load leave balances"));
  }
}

export async function bulkAllocateLeave(data) {
  try {
    const { data: res } = await API.post("/leave/balances/allocate", data);
    return res;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to allocate leave balances"));
  }
}

export async function autoAllocateLeaveBalances(data) {
  const { data: res } = await API.post("/leave/balances/auto-allocate", data);
  return res;
}

export async function fetchHolidayCatalog({ category, state_code } = {}) {
  const { data } = await API.get("/leave/holidays/catalog", {
    params: { category, state_code },
  });
  return data;
}

export async function fetchOrgHolidays({ organisation_id, from_date, to_date }) {
  const { data } = await API.get("/leave/holidays/org", {
    params: { organisation_id, from_date, to_date },
  });
  return data;
}

export async function toggleOrgHoliday(settingId, payload) {
  const { data } = await API.patch(`/leave/holidays/org/${settingId}`, payload);
  return data;
}

export async function runMonthlyAccrual({ organisation_id, year, month, employee_ids }) {
  try {
    const { data } = await API.post("/leave/accruals/run-monthly", {
      organisation_id,
      year,
      month,
      employee_ids: employee_ids || [],
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Monthly accrual failed"));
  }
}

export async function fetchLeaveReportSummary({ organisation_id, from_date, to_date }) {
  const { data } = await API.get("/leave/reports/summary", {
    params: { organisation_id, from_date, to_date },
  });
  return data;
}

export async function fetchAccrualCfPolicy(organisationId, leaveTypeId) {
  const { data } = await API.get("/leave/policies/accrual-cf", {
    params: { organisation_id: organisationId, leave_type_id: leaveTypeId },
  });
  return data;
}

export async function validateAccrualCfPolicy(payload) {
  const { data } = await API.post("/leave/policies/accrual-cf/validate", payload);
  return data;
}

export async function saveAccrualCfPolicy(payload) {
  try {
    const { data } = await API.post("/leave/policies/accrual-cf/save", payload);
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Failed to save accrual policy"));
  }
}

export async function simulateAccrualCfPolicy(payload) {
  try {
    const { data } = await API.post("/leave/policies/accrual-cf/simulate", payload);
    return data;
  } catch (e) {
    throw new Error(errMsg(e, "Simulation failed"));
  }
}

export async function fetchLeaveBalanceTransactions({
  organisation_id,
  employee_id,
  leave_type_id,
  year,
}) {
  const { data } = await API.get("/leave/balances/transactions", {
    params: {
      organisation_id,
      employee_id,
      ...(leave_type_id ? { leave_type_id } : {}),
      ...(year != null ? { year } : {}),
    },
  });
  return data;
}
